import React, { useState } from "react";
import {
  RiFacebookCircleFill,
  RiLinkedinFill,
  RiMailLine,
  RiMapPinLine,
  RiWhatsappFill,
  RiYoutubeFill,
} from "react-icons/ri";
import { Link } from "react-router-dom";
import {
  contactEmail,
  getWhatsAppHref,
  whatsappLabel,
} from "../config/contactChannels";
import { useApi } from "../contexts/ApiProvider";
import { useFlash } from "../contexts/FlashProvider";
import { fallbackServices, normalizeService } from "../helpers/serviceFallbacks";
import { getMarketingAttribution } from "../helpers/marketingAttribution";
import { resolveCmsAsset } from "../cms/media";
import { useCmsGlobal } from "../cms/useCmsContent";

const footerServices = fallbackServices.slice(0, 5).map(normalizeService);

const companyLinks = [
  { label: "À propos", to: "/about" },
  { label: "Nos services", to: "/services" },
  { label: "Réalisations", to: "/realisations" },
  { label: "Documents", to: "/documents" },
  { label: "Contact", to: "/contact" },
];

const legalLinks = [
  { label: "Habilitations", to: "/documents" },
  { label: "Demande de partenariat", to: "/contact" },
  { label: "Support", to: "/contact" },
];

const socialLinks = [
  {
    label: "LinkedIn",
    Icon: RiLinkedinFill,
  },
  {
    label: "Facebook",
    Icon: RiFacebookCircleFill,
  },
  {
    label: "YouTube",
    Icon: RiYoutubeFill,
  },
];

const socialIcons = socialLinks.reduce((acc, link) => {
  acc[link.label] = link.Icon;
  return acc;
}, {});

function FooterLinkList({ title, children }) {
  return (
    <div className="premium-footer-column">
      <h2 className="premium-footer-heading">{title}</h2>
      <ul className="premium-footer-list">{children}</ul>
    </div>
  );
}

function getDynamicWhatsAppHref(contact, message) {
  if (!contact?.whatsapp) return getWhatsAppHref(message);
  return `https://wa.me/${String(contact.whatsapp).replace(/\D/g, "")}${
    message ? `?text=${encodeURIComponent(message)}` : ""
  }`;
}

function NewsletterForm({ footer }) {
  const api = useApi();
  const flash = useFlash();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);

    const response = await api.post("/api/crm/newsletter", {
      email,
      source: "newsletter",
      activityType: "autre",
      priority: "low",
      tags: ["newsletter", "footer"],
      metadata: {
        page: "footer",
        attribution: getMarketingAttribution(),
      },
    });

    setIsSubmitting(false);

    if (response.success) {
      setEmail("");
      flash("Inscription newsletter enregistrée.", "success");
    } else {
      flash(
        response.error?.message ||
          response.message ||
          "Inscription newsletter impossible pour le moment.",
        "danger"
      );
    }
  }

  return (
    <form className="premium-footer-newsletter" onSubmit={handleSubmit}>
      <label htmlFor="footer-newsletter">Newsletter</label>
      <div>
        <input
          id="footer-newsletter"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Votre email"
          required
        />
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "..." : "S'inscrire"}
        </button>
      </div>
      <p>
        {footer?.newsletterText ||
          "Recevez les nouvelles offres, documents et actualités Fatihoune."}
      </p>
    </form>
  );
}

export default function Footer() {
  const { globalContent } = useCmsGlobal();
  const contact = globalContent.contact || {};
  const footer = globalContent.footer || {};
  const navbar = globalContent.navbar || {};
  const socialLinksFromCms = globalContent.socialLinks || [];
  const year = new Date().getFullYear();
  const email = contact.email || contactEmail;
  const whatsappHref = getDynamicWhatsAppHref(contact);
  const whatsappName = contact.whatsappLabel || whatsappLabel;

  return (
    <footer className="premium-footer">
      <div className="premium-footer-cta">
        <div className="container">
          <div className="premium-footer-cta-inner">
            <div>
              <p className="premium-footer-kicker">
                {footer.kicker || "Fatihoune S.A.R.L"}
              </p>
              <h2 className="section-heading section-heading-inverse">
                {footer.title || "Un projet à structurer ou une mission à lancer ?"}
              </h2>
            </div>
            <div className="premium-footer-cta-actions">
              <a
                href={whatsappHref}
                className="btn btn-whatsapp whatsapp-icon-link"
                target="_blank"
                rel="noreferrer"
                aria-label="Contacter Fatihoune sur WhatsApp"
                title="WhatsApp"
              >
                <RiWhatsappFill aria-hidden="true" />
                <span className="visually-hidden">WhatsApp</span>
              </a>
              <Link to="/contact" className="btn btn-success">
                {footer.primaryCta || "Parler à un conseiller"}
              </Link>
              <Link to="/documents" className="btn btn-outline-light">
                {footer.secondaryCta || "Voir les documents"}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="premium-footer-main">
          <div className="premium-footer-brand">
            <Link to="/" className="premium-footer-logo" aria-label="Fatihoune - accueil">
              <img
                src={resolveCmsAsset(navbar.logo, "/assets/images/logo.png")}
                alt="Fatihoune"
              />
            </Link>
            <p>
              {footer.description ||
                "Formation, conseil, assistance et services opérationnels pour accompagner les particuliers, entreprises et institutions avec méthode."}
            </p>
            <div className="premium-footer-contact">
              <a href={`mailto:${email}`}>
                <RiMailLine aria-hidden="true" />
                {email}
              </a>
              <a href={whatsappHref} target="_blank" rel="noreferrer">
                <RiWhatsappFill aria-hidden="true" />
                Nous contacter sur {whatsappName}
              </a>
              <span>
                <RiMapPinLine aria-hidden="true" />
                {contact.address || "Nahda N1 Comp Rabat, Maroc"}
              </span>
            </div>
            <NewsletterForm footer={footer} />
          </div>

          <div className="premium-footer-grid">
            <FooterLinkList title="Services">
              {footerServices.map((service) => (
                <li key={service.slug}>
                  <Link to={`/services/${service.slug}`}>{service.title}</Link>
                </li>
              ))}
              <li>
                <Link to="/services">Tous les services</Link>
              </li>
            </FooterLinkList>

            <FooterLinkList title="Société">
              {companyLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </FooterLinkList>

            <FooterLinkList title="Ressources">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
              <li>
                <a href={whatsappHref} target="_blank" rel="noreferrer">
                  <RiWhatsappFill aria-hidden="true" />
                  Contacter par {whatsappName}
                </a>
              </li>
              <li>
                <a href={`mailto:${email}`}>Écrire par email</a>
              </li>
            </FooterLinkList>
          </div>
        </div>

        <div className="premium-footer-bottom">
          <p>&copy; {year} Fatihoune S.A.R.L. Tous droits réservés.</p>
          <ul className="premium-footer-socials" aria-label="Réseaux sociaux">
            {(socialLinksFromCms.length ? socialLinksFromCms : socialLinks).map((link) => {
              const Icon = link.Icon || socialIcons[link.label] || RiLinkedinFill;
              return (
                <li key={link.label}>
                  <a
                    className="premium-footer-social-placeholder"
                    href={link.url || "#"}
                    target={link.url ? "_blank" : undefined}
                    rel={link.url ? "noreferrer" : undefined}
                    aria-label={link.label}
                    title={link.url ? link.label : `${link.label} - lien à ajouter`}
                  >
                    <Icon aria-hidden="true" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </footer>
  );
}
